import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView } from 'react-native'
import { Button, Text, Spinner, Datepicker, RangeDatepicker } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import MoneyTextInput from '../../components/MoneyTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewTransaction, deleteTransaction } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import { HintView } from '../../components/HintView'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom, localeDateServiceAtom, storesDictAtom, suppliersDictAtom, usersDictAtom, walletsDictAtom } from '../../global/AtomsProvider'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { paymentTransactionDate, paymentTransactionRange } from './paymentTransactionDates'
import { parseDecimalAmount } from '../../utils/decimalInput'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

const TRANSACTION_CATEGORIES = [
  { type: 'expense', linkedDocType: 'workingHour', id: 'salary' },
  { type: 'expense', linkedDocType: 'supply', id: 'supply' },
  { type: 'expense', linkedDocType: 'recurringSpending', id: 'recurringSpending' }
]

const getCategory = (tD, nPD, localCategories) => {
  if (tD) {
    for (let TC of localCategories) {
      if (TC.linkedDocType === tD.linkedDocType) {
        return TC
      }
    }
  }
  if (nPD) {
    for (let TC of localCategories) {
      if (TC.linkedDocType === nPD.linkedDocType) {
        return TC
      }
    }
  }
}

export const getExternalDestinationInfo = (item, usersDict, suppliersDict) => {
  // Always return an object — callers read `.text`, so a missing externalDestination
  // (e.g. a spending with no category, or a deleted user/supplier) must degrade to {}
  // rather than crash the payments list.
  const dest = item.externalDestination
  switch (item.linkedDocType) {
    case 'workingHour':
      return dest?.id ? { ...usersDict[dest.id] } : (dest || {})
    case 'supply':
      return dest?.id ? { ...suppliersDict[dest.id] } : (dest || {})
    case 'recurringSpending':
      return dest || {}
    case 'spending':
      return dest || {}
    default:
      return {}
  }
}

export default function TransactionDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const LOCAL_TRANSACTION_CATEGORIES = TRANSACTION_CATEGORIES.map(category => ({...category, text: t('payments.' + category.id + 'Payment')}))
  const usersDict = useAtomValue(usersDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const walletsDict = useAtomValue(walletsDictAtom)
  const constants = useAtomValue(constantsAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const { detail, newPaymentDetail, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData, preselectedStore } = route.params
  const [category, setCategory] = useState(getCategory(detail, newPaymentDetail, LOCAL_TRANSACTION_CATEGORIES))
  const [storeInfo, setStoreInfo] = useState(detail ? storesDict?.[detail.store] : storesDict?.[(newPaymentDetail?.initialStore ? newPaymentDetail?.initialStore : (preselectedStore || context.user.store.id))])
  const [source, setSource] = useState(detail ? walletsDict?.[detail?.sourceWallet] : null)
  const [externalDestination, setExternalDestination] = useState(detail ? getExternalDestinationInfo(detail, usersDict, suppliersDict) : newPaymentDetail?.externalDestination)
  const [value, setValue] = useState(detail ? detail.value.toString() : (newPaymentDetail?.value?.toString() || ''))
  const [details, setDetails] = useState(detail ? detail.details : null)
  /**
   * ATL-1313 — the pickers below are handed a Date or nothing, never a raw stored value.
   *
   * `x?.toDate?.() || x` decodes a Firestore Timestamp and passes everything else through
   * unchanged, so on the Supabase read path `date` arrives as an ISO string and `paymentRange`'s
   * bounds as either a Postgres timestamptz string or `{_seconds,_nanoseconds}`. UI Kitten's
   * date service formats whatever it is given through `date-fns`, which THROWS on a value that is
   * not a valid Date — and this screen is what the crashing list navigates into, so the guard has
   * to hold on both. `toNestedDate` returns null for an unreadable value and the picker then
   * shows its placeholder, which is the honest rendering of a date nobody can read.
   */
  const [date, setDate] = useState(() => (detail ? paymentTransactionDate(detail) : new Date()))
  const [range, setRange] = useState(() => {
    const decoded = detail ? paymentTransactionRange(detail) : null
    if (decoded) return { startDate: decoded.startDate, endDate: decoded.endDate }
    return detail ? {} : (newPaymentDetail?.paymentRange || {})
  })
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const _datePicker = React.createRef()
  const _rangePicker = React.createRef()
  

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        detail != null && <HeaderIconButton
          onPress={async () => {
            if (!isLoading) {
              try {
                setIsLoading(true)
                await deleteTransaction(db, detail.ref, detail)
                removeItemFromData?.(sectionIndex, itemIndex)
                setIsLoading(false)
                navigation.goBack()
              } catch(error) {
                console.log(error)
                setSaveError(t('error'))
                Sentry.captureException(error, { tags: { books_write_error: 'payment-transaction-delete' } })
                setIsLoading(false)
              }
            }
          }}
          iconName={isLoading ? 'loader-outline' : 'trash-outline'}
        />
      ),
    })
  }, [isLoading])

  async function save() {
    if (detail == null) {
      setIsLoading(true)
      // ATL-1371 — a STRICT parse, never `parseFloat`. The decimal key a keypad renders is the
      // device locale's separator, so this box legitimately holds '36,5' on a ru/kk phone;
      // `parseFloat('36,5')` is 36, which is not NaN and so passed every guard here and was paid
      // to a supplier as 36. `parseDecimalAmount` returns null instead, and null refuses the save.
      const numberValue = parseDecimalAmount(value)
      if (value !== '' && numberValue === null) {
        setSaveError(t('notANumberError'))
        setIsLoading(false)
        return
      }
      // The amount must be POSITIVE, and a strict parse cannot enforce that — `-36.5` is a
      // perfectly well-formed decimal. This is not a hypothetical paste, it is a two-tap path:
      // `PaymentStatsScreen.js:1351`'s «оплатить» shortcut opens this screen with the amount
      // PREFILLED to `totalDebit - totalCreditCurrentPeriod`, which is 0 for a settled supplier
      // and NEGATIVE for an over-paid one. Saving that booked an expense of −36.5 AND — because
      // `saveNewTransaction` flips every unpaid supply in the payment range to `isPaid: true`
      // irrespective of the value (`firebase.ts:5102`) — marked those supplies paid at the same
      // time. Two wrong numbers in the books from a button that offers to fill the form for you.
      //
      // The zero case had its own defect: it fell through to `notAllFieldsFilled` while every
      // field WAS filled, sending the merchant hunting for a field that is not missing.
      if (numberValue !== null && numberValue <= 0) {
        setSaveError(t('amountMustBePositive'))
        setIsLoading(false)
        return
      }
      // ATL-1590 — the store is a required field like the others. The Cash Flow report's «оплатить»
      // opens this screen with `initialStore: 'all'` under «Все», which names no store, so
      // `storeInfo` starts undefined; `storeInfo.id` below then threw outside the try and left the
      // spinner on with nothing saved. Reachable since hourly/daily salaries prefill a positive amount.
      if (numberValue !== null && storeInfo != null && source != null && category != null && range.startDate != null && range.endDate != null && externalDestination != null) {
        setSaveError('')
        const info = {
          linkedDocType: category.linkedDocType,
          type: category.type,
          value: numberValue,
          sourceWallet: source.id,
          externalDestination,
          date: date,
          dailyStatID: getDailyStatID(storeInfo.id, date),
          paymentRange: range,
          details: details,
          paidBy: context.user.uid,
          store: storeInfo.id,
          organizationId: context.user.organizationId,
          createdDate: new Date(),
          updatedDate: new Date(),
          createdBy: context.user.uid,
          updatedBy: context.user.uid,
        }
        try {
          let ref = await saveNewTransaction(db, info)
          addItemToData?.({...info, ref, id: ref.id}, info.date, info.store)
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'payment-transaction-create' } })
          console.log(error)
          setSaveError(saveFailureText(error, t, t('error')))
          setIsLoading(false)
        }
      } else {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
      }
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
        { Object.keys(storesDict).length > 1 && (
          <>
            <Text category='label' style={styles.label}>
              {t('store')}
            </Text>
            <View testID="txn-store-select">
            <PlatformDependantSelect
              data={Object.values(storesDict)}
              selectedOption={storeInfo}
              disabled={detail != null}
              onSelect={(itemValue, itemIndex) => {
                setStoreInfo(itemValue)
              }}
            />
            </View>
          </>
        )}
        <Text category='label' style={styles.label}>
          {t('payments.category')}
        </Text>
        <View testID="txn-category-select">
        <PlatformDependantSelect
          data={LOCAL_TRANSACTION_CATEGORIES}
          selectedOption={category}
          disabled={detail != null}
          onSelect={(itemValue, itemIndex) => {
            setCategory(itemValue)
          }}
        />
        </View>
        { category && (
          <Text category='label' style={styles.label}>
            {t('payments.destination')}
          </Text>
        )}
        { category?.id === 'salary' && (
          <PlatformDependantSelect
            data={Object.values(usersDict)}
            selectedOption={externalDestination}
            onSelect={setExternalDestination}
            disabled={detail != null}
            withSearch={true}
          />
        )}
        { category?.id === 'supply' && (
          <View testID="txn-destination-select">
          <PlatformDependantSelect
            data={Object.values(suppliersDict)}
            selectedOption={externalDestination}
            onSelect={setExternalDestination}
            disabled={detail != null}
            withSearch={true}
          />
          </View>
        )}
        { category?.id === 'recurringSpending' && (
          <PlatformDependantSelect
            data={constants?.recurringSpendings.data}
            selectedOption={externalDestination}
            onSelect={setExternalDestination}
            disabled={detail != null}
            titleKey='name'
            withSearch={true}
          />
        )}
        <Text category='label' style={styles.label}>
          {t('payments.amount')}
        </Text>
        <MoneyTextInput
          value={value}
          onChangeText={text => setValue(text)}
          placeholder='0'
          returnKeyType='done'
          disabled={detail != null}
          selectTextOnFocus={true}
        />
        <Text category='label' style={styles.label}>
          {t('payments.sourceWallet')}
        </Text>
        <View testID="txn-source-select">
        <PlatformDependantSelect
          data={constants?.wallets.data}
          selectedOption={source}
          disabled={detail != null}
          onSelect={(itemValue, itemIndex) => {
            setSource(itemValue)
          }}
        />
        </View>
        <Text category='label' style={styles.label}>
          {t('payments.paymentDate')}
        </Text>
        <Datepicker
          placement='top'
          date={date}
          dateService={localeDateService}
          disabled={detail != null}
          onSelect={(date) => {
            setDate(date)
            _datePicker.current.blur()
          }}
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          ref={_datePicker}
        />
        <Text category='label' style={styles.label}>
          {t('payments.paymentRange')}
        </Text>
        <View testID="txn-range-picker">
        <RangeDatepicker
          placement='top'
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          range={range}
          ref={_rangePicker}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              _rangePicker.current.blur()
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              _rangePicker.current.blur()
            } else {
              setRange(newRange)
            }
          }}
        />
        </View>
        {category?.id === 'supply' && <HintView
          text={t('payments.supplyPaymentRangeInfo')} 
          iconName='alert-circle-outline' style={{marginVertical: 10}} 
        />}
        <Text category='label' style={styles.label}>
          {t('payments.details')}
        </Text>
        <StyledTextInput 
          value={details}
          onChangeText={text => setDetails(text)}
          returnKeyType='done'
          disabled={detail != null}
          selectTextOnFocus={true}
        />
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        {successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && <Button testID="txn-save-button" onPress={save} style={styles.submitButton} disabled={detail != null}>
            {t('save')}
          </Button>
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  }
});